import { useState, useEffect } from 'react';
import type { ReactNode } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import ScheduleIcon from '@mui/icons-material/Schedule';
import LocalAtmIcon from '@mui/icons-material/LocalAtm';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import StyleIcon from '@mui/icons-material/Style';

import { useGetAvailableCouponsMutation } from '#services/coupon';
import { formatCurrency, formatDate } from '#common/utils';
import {
  COUPON_DISCOUNT_TYPES,
  COUPON_TARGET_TYPES,
} from '#common/constants';

type TFn = ReturnType<typeof useTranslation>['t'];

interface CouponData {
  id?: number | string;
  code?: string;
  name?: string;
  description?: string;
  discountType?: string;
  discountValue?: number;
  targetType?: string;
  targetNames?: string[];
  endDate?: string | number | Date;
  minPurchaseAmount?: number;
  isExclusive?: boolean;
  [key: string]: unknown;
}

const NAVY_INK = '#1e3f58';
const GOLD = '#d9a258';
const GOLD_LIGHT = '#e8b96e';
const TEXT_DESC_ON_NAVY = 'rgba(255, 255, 255, 0.78)';
const TEXT_META_ON_NAVY = 'rgba(255, 255, 255, 0.72)';
const TEXT_APPLIED_ON_NAVY = 'rgba(255, 255, 255, 0.55)';

export function useAvailableCoupons() {
  const [coupons, setCoupons] = useState<CouponData[]>([]);
  const [loading, setLoading] = useState(true);

  const userInfo = useSelector(
    (state: { auth: { userInfo?: { id?: number | string; username?: string } | null } }) => state.auth.userInfo,
  );
  const [getAvailableCoupons] = useGetAvailableCouponsMutation();

  useEffect(() => {
    if (!userInfo?.id) {
      setLoading(false);
      return;
    }

    void getAvailableCoupons({})
      .then((res) => {
        const resData = ('data' in res ? res.data : undefined) as { error?: unknown; data?: CouponData[] } | undefined;
        if (resData && !resData.error) {
          const data = resData.data || resData;
          setCoupons(Array.isArray(data) ? data : []);
        }
      })
      .finally(() => {
        setLoading(false);
      });
  }, [userInfo?.id]);

  return { coupons, loading };
}

function getScopeLabel(coupon: CouponData, t: TFn) {
  if (coupon.targetType === COUPON_TARGET_TYPES.CART) {
    return t('scope.fullCart');
  }
  const names = coupon.targetNames || [];
  if (coupon.targetType === COUPON_TARGET_TYPES.COLLECTION) {
    if (names.length === 0) return t('scope.allCollections');
    if (names.length === 1) return names[0];
    return t('scope.moreCollections', { first: names[0], count: names.length - 1 });
  }
  // PRODUCT
  if (names.length === 0) return t('scope.specificProducts');
  if (names.length === 1) return names[0];
  return t('scope.specificProductsCount', { count: names.length });
}

function getDaysUntilExpiry(endDate: string | number | Date) {
  const now = new Date();
  const end = new Date(endDate);
  const diff = end.getTime() - now.getTime();
  return Math.ceil(diff / (1000 * 60 * 60 * 24));
}

function getExpiryChip(coupon: CouponData, t: TFn) {
  const days = getDaysUntilExpiry(coupon.endDate as string | number | Date);
  if (days <= 0) return null;

  if (days <= 3) {
    return (
      <Chip
        size="small"
        label={days === 1 ? t('expiry.endsTomorrow') : t('expiry.daysLeft', { days })}
        sx={{
          ml: 0.75,
          height: 18,
          fontSize: '0.6rem',
          borderRadius: 5,
          bgcolor: 'rgba(255, 99, 71, 0.18)',
          color: '#ff8b73',
          border: '1px solid rgba(255, 99, 71, 0.4)',
        }}
      />
    );
  }

  if (days <= 7) {
    return (
      <Chip
        size="small"
        label={t('expiry.daysLeft', { days })}
        sx={{
          ml: 0.75,
          height: 18,
          fontSize: '0.6rem',
          borderRadius: 5,
          bgcolor: 'rgba(217, 162, 88, 0.2)',
          color: GOLD_LIGHT,
          border: '1px solid rgba(217, 162, 88, 0.5)',
        }}
      />
    );
  }

  return null;
}

interface CouponCardProps {
  coupon: CouponData;
  customerName?: string | undefined;
}

function CouponCard({ coupon, customerName }: CouponCardProps) {
  const { t } = useTranslation('coupon');
  const exclusiveLabel = coupon.isExclusive && customerName
    ? t('card.exclusive', { name: customerName.toUpperCase() })
    : t('card.promotion');

  const discountDisplay: { value: string; suffix: string | null } = coupon.discountType === COUPON_DISCOUNT_TYPES.PERCENT
    ? { value: `${coupon.discountValue}`, suffix: '%' }
    : { value: formatCurrency(coupon.discountValue as number), suffix: null };

  return (
    <Box
      sx={{
        position: 'relative',
        display: 'flex',
        minHeight: 148,
        borderRadius: 2,
        overflow: 'hidden',
        bgcolor: NAVY_INK,
        color: '#fff',
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.08)',
        transition: 'transform 180ms ease, box-shadow 180ms ease',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: '0 6px 18px rgba(29, 58, 82, 0.25)',
        },
        // Soft gold sweep in the corners to echo the brand gradient
        '&::before': {
          content: '""',
          position: 'absolute',
          inset: 0,
          background: `
            radial-gradient(ellipse at 100% 0%, rgba(232,185,110,0.22) 0 30%, transparent 55%),
            radial-gradient(ellipse at 0% 100%, rgba(217,162,88,0.14) 0 30%, transparent 55%)
          `,
          pointerEvents: 'none',
        },
      }}
    >
      {/* Body */}
      <Box
        sx={{
          position: 'relative',
          zIndex: 1,
          flex: 1,
          minWidth: 0,
          px: { xs: 2, sm: 2.5 },
          py: { xs: 1.75, sm: 2 },
          display: 'flex',
          flexDirection: 'column',
          gap: 0.75,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <Box
            sx={{
              width: 6,
              height: 6,
              borderRadius: '50%',
              bgcolor: GOLD,
              flexShrink: 0,
            }}
          />
          <Typography
            sx={{
              fontSize: '0.65rem',
              fontWeight: 700,
              letterSpacing: '0.12em',
              color: GOLD_LIGHT,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {exclusiveLabel}
          </Typography>
        </Box>

        <Typography
          sx={{
            fontSize: { xs: '1rem', sm: '1.125rem' },
            fontWeight: 400,
            color: '#fff',
            lineHeight: 1.3,
            letterSpacing: '0.01em',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {coupon.name || coupon.code}
        </Typography>

        {coupon.description && (
          <Typography
            sx={{
              fontSize: '0.8125rem',
              color: TEXT_DESC_ON_NAVY,
              lineHeight: 1.5,
              maxWidth: '38ch',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}
          >
            {coupon.description}
          </Typography>
        )}

        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '10px 18px',
            mt: 'auto',
            pt: 1,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <ScheduleIcon sx={{ fontSize: 14, color: GOLD_LIGHT }} />
            <Typography sx={{ fontSize: '0.75rem', lineHeight: 1.3, color: TEXT_META_ON_NAVY }}>
              {t('card.expires', { date: formatDate(coupon.endDate as string | number | Date) })}
            </Typography>
            {getExpiryChip(coupon, t)}
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <LocalAtmIcon sx={{ fontSize: 14, color: GOLD_LIGHT }} />
            <Typography sx={{ fontSize: '0.75rem', lineHeight: 1.3, color: TEXT_META_ON_NAVY }}>
              {(coupon.minPurchaseAmount || 0) > 0
                ? t('card.minOrder', { amount: formatCurrency(coupon.minPurchaseAmount as number) })
                : t('card.noMinimum')}
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
            <StyleIcon sx={{ fontSize: 14, color: GOLD_LIGHT, flexShrink: 0 }} />
            <Typography
              sx={{
                fontSize: '0.75rem',
                lineHeight: 1.3,
                color: TEXT_META_ON_NAVY,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {getScopeLabel(coupon, t)}
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Dashed divider */}
      <Box
        sx={{
          borderLeft: '1px dashed rgba(255, 255, 255, 0.25)',
          flexShrink: 0,
        }}
      />

      {/* Right stub: discount + code */}
      <Box
        sx={{
          position: 'relative',
          zIndex: 1,
          width: { xs: 120, sm: 132 },
          flexShrink: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 0.75,
          px: 1.5,
          py: 2,
          textAlign: 'center',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'center' }}>
          <Typography
            sx={{
              fontWeight: 400,
              fontSize: discountDisplay.value.length > 4 ? '1.75rem' : '2.375rem',
              lineHeight: 1,
              letterSpacing: '-0.01em',
              color: GOLD_LIGHT,
              whiteSpace: 'nowrap',
            }}
          >
            {discountDisplay.value}
          </Typography>
          {discountDisplay.suffix && (
            <Typography
              sx={{
                fontWeight: 400,
                fontSize: '1.25rem',
                color: GOLD_LIGHT,
                ml: '2px',
              }}
            >
              {discountDisplay.suffix}
            </Typography>
          )}
        </Box>

        <Box
          sx={{
            border: '1px dashed rgba(232, 185, 110, 0.55)',
            borderRadius: '3px',
            bgcolor: 'rgba(0, 0, 0, 0.18)',
            px: 1.25,
            py: 0.75,
            maxWidth: '100%',
          }}
        >
          <Typography
            sx={{
              fontFamily: 'monospace',
              fontWeight: 500,
              fontSize: '0.75rem',
              lineHeight: 1,
              letterSpacing: '0.14em',
              color: '#fff',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {coupon.code}
          </Typography>
        </Box>

        {coupon.isExclusive && (
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.5,
              color: TEXT_APPLIED_ON_NAVY,
            }}
          >
            <CheckCircleIcon sx={{ fontSize: 12 }} />
            <Typography
              sx={{
                fontSize: '0.625rem',
                lineHeight: 1.2,
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
              }}
            >
              {t('card.autoApplied')}
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
}

interface CouponBannerProps {
  announcement?: ReactNode;
}

/**
 * CouponBanner — composes the optional announcement card with available coupons.
 * Accepts an `announcement` JSX node so callers can place announcements alongside coupons.
 */
export default function CouponBanner({ announcement = null }: CouponBannerProps) {
  const { coupons, loading } = useAvailableCoupons();
  const userInfo = useSelector(
    (state: { auth: { userInfo?: { username?: string } | null } }) => state.auth.userInfo,
  );

  if (loading || coupons.length === 0) {
    return announcement
      ? <Box sx={{ mb: { xs: 2, sm: 3 } }}>{announcement}</Box>
      : null;
  }

  const customerName = userInfo?.username;

  // 1 coupon + announcement → side by side, each at natural height
  if (announcement && coupons.length === 1) {
    return (
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 1.35fr) minmax(0, 1fr)' },
          alignItems: 'start',
          gap: { xs: 1.5, sm: 2 },
          mb: { xs: 2, sm: 3 },
        }}
      >
        {announcement}
        <CouponCard coupon={coupons[0]!} customerName={customerName} />
      </Box>
    );
  }

  // Otherwise: announcement (if any) on its own row, coupons in a grid below
  return (
    <Box sx={{ mb: { xs: 2, sm: 3 } }}>
      {announcement && <Box sx={{ mb: { xs: 1.5, sm: 2 } }}>{announcement}</Box>}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            sm: 'repeat(auto-fill, minmax(360px, 1fr))',
          },
          gap: { xs: 1.5, sm: 2 },
        }}
      >
        {coupons.map((coupon) => (
          <CouponCard key={coupon.id} coupon={coupon} customerName={customerName} />
        ))}
      </Box>
    </Box>
  );
}
